import type { ColumnFiltersState } from '@tanstack/react-table';
import type { DifferentialScenarioParams } from './atoms';

/**
 * Translates a DataTable column id (camelCase) to the BE's snake_case sort
 * attribute name. The BE validates `sort_attribute` against an allow-list of
 * snake_case fields (`SORTABLE_FIELDS` in `Query::DifferentialScenarios`);
 * Rails Boost auto-transforms request KEYS but not values, so we translate
 * here.
 */
export const SORT_FIELD_MAP: Record<string, string> = {
  name: 'name',
  effectiveDate: 'effective_date',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
};

/**
 * Maps DataTable column filter state to differential scenario query params.
 * Always emits all four filter keys — set to a value for filters present in
 * `filters`, and explicitly `undefined` for filters that were cleared — so the
 * caller can spread the result and overwrite any stale constraints.
 * @param {ColumnFiltersState} filters - The column filter state from DataTable
 * @return {Partial<DifferentialScenarioParams>} A patch covering the four filter keys
 */
export const columnFiltersToParams = (
  filters: ColumnFiltersState
): Partial<DifferentialScenarioParams> => {
  const params: Partial<DifferentialScenarioParams> = {
    name: undefined,
    createdBy: undefined,
    createdAt: undefined,
    updatedAt: undefined,
    effectiveDate: undefined,
  };

  for (const filter of filters) {
    switch (filter.id) {
      case 'name':
        params.name = (filter.value as string) || undefined;
        break;
      case 'createdBy':
        params.createdBy = filter.value as string[];
        break;
      case 'createdAt':
      case 'updatedAt':
      case 'effectiveDate': {
        const [start, end] =
          (filter.value as [string | null, string | null]) ?? [];
        params[filter.id] =
          start || end
            ? { start: start ?? undefined, end: end ?? undefined }
            : undefined;
        break;
      }
    }
  }

  return params;
};
